import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronDown, Copy, Key } from "react-feather";
import { copySchedulingAccess, getSchedulingAccess, revokeSchedulingAccess, rotateSchedulingAccess, SchedulingApiError } from "../store/api";
import { copyPromisedTextToClipboard, copyTextToClipboard } from "../lib/clipboard";
import type { SchedulingAccess } from "../lib/types";
import "../styles/scheduling-agent.css";

interface Props { chatId: string; chatName: string; isCurrentChat: () => boolean }
export default function ScheduleAccess(props: Props) {
  return <ChatScheduleAccess key={props.chatId} {...props} />;
}
const date = (value: number | null) => value == null ? "Never" : new Date(value).toLocaleString();

function ChatScheduleAccess({ chatId, chatName, isCurrentChat }: Props) {
  const [access, setAccess] = useState<SchedulingAccess | null>();
  const [expanded, setExpanded] = useState(false);
  const [confirm, setConfirm] = useState<"rotate" | "revoke" | null>(null);
  const [busy, setBusy] = useState<"copy" | "rotate" | "revoke" | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const mounted = useRef(false), sequence = useRef(0), pending = useRef(false);
  const currentChat = useRef(isCurrentChat);
  useLayoutEffect(() => { currentChat.current = isCurrentChat; }, [isCurrentChat]);
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const current = () => mounted.current && currentChat.current();

  useEffect(() => {
    const request = ++sequence.current;
    void getSchedulingAccess(chatId).then(result => {
      if (mounted.current && currentChat.current() && request === sequence.current) setAccess(result.access);
    }).catch(() => {
      if (mounted.current && currentChat.current() && request === sequence.current) setError("Could not load this connection. Refresh before changing it.");
    });
  }, [chatId]);

  async function refreshAccess(clearError = true) {
    const request = ++sequence.current;
    try {
      const result = await getSchedulingAccess(chatId);
      if (current() && request === sequence.current) { setAccess(result.access); if (clearError) setError(""); return true; }
    } catch {
      if (current()) setError("Could not refresh access. No further access changes were made.");
    }
    return false;
  }

  async function copy(mode: "copy" | "rotate") {
    if (pending.current || !current() || (mode === "rotate" && !access?.active)) return;
    pending.current = true; sequence.current++;
    setBusy(mode); setNotice(""); setError(""); setConfirm(null);
    // The promised ClipboardItem is started during the original user gesture.
    // setup is transient: never React state, a URL, storage, or a chat message.
    const response = mode === "rotate" ? rotateSchedulingAccess(chatId, access!.keyId) : copySchedulingAccess(chatId);
    const setup = response.then(result => {
      if (!current()) throw new Error("stale-chat");
      if (typeof result.setup !== "string" || !result.setup.trim()) throw new Error("invalid-setup");
      return result.setup;
    });
    const clipboard = copyPromisedTextToClipboard(setup);
    try {
      const [result, text] = await Promise.all([response, setup]);
      if (!current()) return;
      setAccess(result.access);
      let copied = clipboard ? await clipboard : false;
      if (!current()) return;
      if (!copied) copied = await copyTextToClipboard(text);
      if (!current()) return;
      if (copied) setNotice(mode === "rotate" ? "New connection details copied. The old key no longer works. No task created yet." : "Connection details copied — no task created yet.");
      else setError(mode === "rotate" ? "The key was rotated, but copying was blocked. Tap Copy Schedule to copy the same new key." : "Clipboard blocked. Tap Copy Schedule again; this does not rotate an active key.");
    } catch (cause) {
      if (!current()) return;
      setError(cause instanceof SchedulingApiError ? cause.message : "Access could not be copied. Refresh access before retrying.");
      await refreshAccess(false);
    } finally {
      pending.current = false;
      if (current()) setBusy(null);
    }
  }

  async function revoke() {
    if (pending.current || !current() || !access?.active) return;
    pending.current = true; sequence.current++;
    setBusy("revoke"); setNotice(""); setError(""); setConfirm(null);
    try {
      const result = await revokeSchedulingAccess(chatId, access.keyId);
      if (current()) { setAccess(result.access); setNotice("Connection revoked. Existing schedules continue until you pause or cancel them."); }
    } catch (cause) {
      if (current()) { setError(cause instanceof SchedulingApiError ? cause.message : "Revocation could not be confirmed. Refresh access before retrying."); await refreshAccess(false); }
    } finally { pending.current = false; if (current()) setBusy(null); }
  }

  return <section className="schedule-access" aria-label={`Schedule with an agent for ${chatName}`}>
    <div className="schedule-access__bar">
      <div className="schedule-access__context"><strong>Let an agent schedule it</strong><span title={chatName}>For {chatName}</span></div>
    </div>
    <ol className="schedule-access__steps" aria-label="How to schedule with an agent">
      <li><b aria-hidden="true">1</b><span><strong>Copy Schedule</strong><small>Get this chat’s connection</small></span></li>
      <li><b aria-hidden="true">2</b><span><strong>Paste to a trusted agent</strong><small>Keep the details private</small></span></li>
      <li><b aria-hidden="true">3</b><span><strong>Describe your schedule</strong><small>Your agent saves the task here</small></span></li>
    </ol>
    <button type="button" className="schedule-agent__pill schedule-agent__pill--primary schedule-access__copy" onClick={() => void copy("copy")} disabled={busy !== null} aria-busy={busy === "copy"}>
      {notice.startsWith("Connection details copied") ? <Check size={17} aria-hidden="true" /> : <Copy size={17} aria-hidden="true" />}
      {busy === "copy" ? "Copying…" : "Copy Schedule"}
    </button>
    <p className="schedule-agent__hint schedule-access__privacy">Only share with an agent you trust. Anyone with these details can schedule prompts using this chat’s existing access.</p>
    {notice && <p className="schedule-agent__notice" role="status">{notice}</p>}
    {error && <div className="schedule-agent__error" role="alert"><p>{error}</p><button type="button" className="schedule-agent__link" disabled={busy !== null} onClick={() => void refreshAccess()}>Refresh access</button></div>}
    <button type="button" className="schedule-agent__link schedule-access__settings" aria-label="Connection settings" aria-expanded={expanded} onClick={() => { setExpanded(!expanded); setConfirm(null); }}>
      <Key size={15} aria-hidden="true" /> Connection settings <ChevronDown size={15} aria-hidden="true" className={expanded ? "is-expanded" : ""} />
      {access?.active && <span className="schedule-access__connected">Connected</span>}
    </button>
    {expanded && <div className="schedule-access__manage">
      <dl className="schedule-agent__facts">
        <div><dt>Status</dt><dd>{access === undefined ? "Not loaded" : access === null ? "Not enabled" : access.active ? "Active until revoked" : "Revoked"}</dd></div>
        {access && <div><dt>Created</dt><dd>{date(access.createdAt)}</dd></div>}
      </dl>
      <p className="schedule-agent__hint">{access?.active === false ? "Copy Schedule creates a new connection after revocation." : "Copy Schedule enables the connection if needed and reuses an active key."} Copying connection details does not create a task.</p>
      <p className="schedule-agent__hint">Rotating replaces the key immediately. Revoking blocks this connection only. Existing schedules continue; pause or cancel them separately. Already delivered prompts cannot be recalled.</p>
      {access?.active && !confirm && <div className="schedule-agent__actions">
        <button type="button" className="schedule-agent__pill" disabled={busy !== null} onClick={() => setConfirm("rotate")}>Rotate key</button>
        <button type="button" className="schedule-agent__pill schedule-agent__pill--danger" disabled={busy !== null} onClick={() => setConfirm("revoke")}>Revoke access</button>
      </div>}
      {confirm && <div className="schedule-agent__confirm" role="group" aria-label={confirm === "rotate" ? "Confirm key rotation" : "Confirm access revocation"}>
        <strong>{confirm === "rotate" ? "Replace this chat’s key?" : "Disconnect external agents?"}</strong>
        <p>{confirm === "rotate" ? "External agents using the old key lose access immediately. The new connection details will be copied." : "External agents lose this connection. This does not pause or cancel existing schedules."}</p>
        <div className="schedule-agent__actions">
          <button type="button" className="schedule-agent__pill" onClick={() => setConfirm(null)}>Keep current access</button>
          <button type="button" className="schedule-agent__pill schedule-agent__pill--danger" onClick={() => confirm === "rotate" ? void copy("rotate") : void revoke()}>{confirm === "rotate" ? "Rotate & copy" : "Revoke access now"}</button>
        </div>
      </div>}
    </div>}
  </section>;
}
